import { useState, useCallback } from 'react';
import { DefaultValue, DefaultValues, UpdateEntityPayload } from '../../schemas/entity';
import { EntityRecord } from '../../api/api-entities';
import { FieldEditor } from './field-editor';
import { getDefaultValueForType } from './utils';
import { Button, Collapsible, CollapsibleContent, CollapsibleTrigger } from '@@/ui/primitives';
import { ChevronDownIcon, ChevronUpIcon, LigatureIcon } from 'lucide-react';
import { cn } from '@@/lib/utils';

interface DefaultValuesProps {
  entity: EntityRecord;
  onSave: (defaults: UpdateEntityPayload['defaults']) => void;
}

// type ChangedField = {
//   value?: any;
//   overridable: boolean;
//   source: 'user' | 'inherited';
//   modifiable: boolean;
// };

export function DefaultValuesComponent({ entity, onSave }: DefaultValuesProps) {
  const [isOpen, setOpen] = useState(false);
  const [changedFields, setChangedFields] = useState<Record<string, DefaultValue>>({});
  const [removedFields, setRemovedFields] = useState<Set<string>>(new Set());
  const [_editingFields, setEditingFields] = useState<Set<string>>(new Set());

  const defaults: DefaultValues = entity.defaults || {};

  const updateField = useCallback(
    (fieldName: string, updates: Partial<DefaultValue>) => {
      const currentDefault = defaults[fieldName];
      const currentChanged = changedFields[fieldName];

      const newField: DefaultValue = {
        value: updates.value ?? currentChanged?.value ?? currentDefault?.value,
        overridable: updates.overridable ?? currentChanged?.overridable ?? currentDefault?.overridable ?? true,
        source: updates.source ?? currentChanged?.source ?? currentDefault?.source ?? 'user',
        modifiable: defaults[fieldName]?.modifiable ?? true
      };

      // If source is inherited, don't include value in the payload
      if (newField.source === 'inherited') {
        newField.value = null;
      }

      // Check if this is different from the original
      const isValueChanged = newField.value !== currentDefault?.value;
      const isOverridableChanged = newField.overridable !== currentDefault?.overridable;
      const isSourceChanged = newField.source !== currentDefault?.source;

      if (!isValueChanged && !isOverridableChanged && !isSourceChanged) {
        // Back to original, remove from changes
        setChangedFields((prev) => {
          const newFields = { ...prev };
          delete newFields[fieldName];
          return newFields;
        });
      } else {
        setChangedFields((prev) => ({
          ...prev,
          [fieldName]: newField
        }));
      }
    },
    [defaults, changedFields]
  );

  const handleValueChange = useCallback(
    (fieldName: string, value: DefaultValue['value']) => {
      updateField(fieldName, { value, source: 'user' });
    },
    [updateField]
  );

  const handleOverridableChange = useCallback(
    (fieldName: string, overridable: boolean) => {
      updateField(fieldName, { overridable });
    },
    [updateField]
  );

  const handleSourceChange = useCallback(
    (fieldName: string, source: 'user' | 'inherited') => {
      updateField(fieldName, { source });
    },
    [updateField]
  );

  const handleClear = useCallback((fieldName: string) => {
    setRemovedFields((prev) => new Set(prev).add(fieldName));
    setChangedFields((prev) => {
      const newFields = { ...prev };
      delete newFields[fieldName];
      return newFields;
    });
  }, []);

  const handleAddDefault = useCallback(
    (fieldName: string) => {
      const fieldType = entity.schema.fields[fieldName].definition.type;
      const defaultValue = getDefaultValueForType(fieldType);
      updateField(fieldName, {
        value: defaultValue,
        overridable: true,
        source: 'user'
      });
    },
    [entity.schema.fields, updateField]
  );

  const handleEditField = useCallback(
    (fieldName: string) => {
      const currentDefault = defaults[fieldName];
      if (currentDefault) {
        // Add to editing fields to show edit UI
        setEditingFields((prev) => new Set(prev).add(fieldName));

        // Also add to changedFields if not already there, so we have the values to edit
        if (!changedFields[fieldName]) {
          setChangedFields((prev) => ({
            ...prev,
            [fieldName]: {
              value: currentDefault.value,
              overridable: currentDefault.overridable,
              source: currentDefault.source,
              modifiable: currentDefault.modifiable
            }
          }));
        }
      }
    },
    [defaults, changedFields]
  );

  const handleSave = useCallback(() => {
    onSave(changedFields);
    setChangedFields({});
    setRemovedFields(new Set());
  }, [changedFields, onSave]);

  const handleCancel = useCallback(() => {
    setChangedFields({});
    setRemovedFields(new Set());
  }, []);

  const enrichedDefaults = [];
  for (const [fieldName, defaultValue] of Object.entries(entity.defaults)) {
    const correspondingField = entity.schema.fields[fieldName];
    if (!correspondingField) {
      console.warn('No corresponding field found in schema for default:', fieldName, defaultValue);
      continue;
    }

    const fieldType = correspondingField.definition.type;
    enrichedDefaults.push({
      fieldName: fieldName,
      defaultValue: defaultValue,
      fieldType: fieldType
    });
  }

  if (enrichedDefaults.length === 0) {
    return null;
  }

  const hasChanges = Object.keys(changedFields).length > 0 || removedFields.size > 0;

  return (
    <div className={cn('rounded-md border border-slate-300 flex flex-col overflow-auto', !isOpen && 'shadow-xs')}>
      <Collapsible open={isOpen} onOpenChange={setOpen}>
        <CollapsibleTrigger className='w-full'>
          <div className='flex items-center gap-3 justify-between px-3 text-slate-950 h-10 bg-slate-50 hover:bg-slate-100 cursor-pointer'>
            <div className='flex items-center gap-2'>
              <LigatureIcon className='size-4' />
              <h3 className='text-sm font-semibold leading-snug'>Defaults</h3>
              <p className='text-sm text-slate-600 ml-1 leading-snug'>Default property values for {entity.name}.</p>
            </div>
            {isOpen ? <ChevronUpIcon className='size-4' /> : <ChevronDownIcon className='size-4' />}
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className='py-2 bg-slate-50 flex flex-col'>
          <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 py-1 bg-slate-50 gap-4'>
            {enrichedDefaults.map(({ fieldName, defaultValue, fieldType }) => {
              const isRemoved = removedFields.has(fieldName);
              const changedValue = changedFields[fieldName];

              return (
                <FieldEditor
                  entity={entity}
                  key={fieldName}
                  fieldName={fieldName}
                  hasParent={entity.parentId !== 'root'}
                  fieldType={fieldType}
                  field={entity.schema.fields[fieldName]}
                  currentValue={defaultValue}
                  editValue={changedValue}
                  isEditing={!!changedValue || isRemoved}
                  onValueChange={handleValueChange}
                  onOverridableChange={handleOverridableChange}
                  onSourceChange={handleSourceChange}
                  onCancel={handleCancel}
                  onClear={handleClear}
                  onEdit={handleEditField}
                  onAdd={handleAddDefault}
                />
              );
            })}
          </div>
          {hasChanges && (
            <div className='flex items-center gap-2 px-4 py-2 self-end'>
              <>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={handleCancel}
                  className='text-sm! font-medium! rounded-full h-7'
                >
                  Cancel
                </Button>
                <Button
                  variant='default'
                  size='sm'
                  onClick={handleSave}
                  className='text-sm! font-medium! rounded-full px-3! h-7'
                >
                  Save
                </Button>
              </>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
